import type { CalendarEventFeedbackQuestion } from 'shared-types';

type Params = {
  data: unknown;
  isRequired: boolean;
  type: CalendarEventFeedbackQuestion['type'];
};

const isWholeNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && Boolean(value.trim());

// an option needs a generated id to match answers against and text to show, so
// neither may be blank
const isOption = (option: unknown) =>
  Boolean(option) &&
  typeof option === 'object' &&
  isNonEmptyString((option as { id: unknown }).id) &&
  isNonEmptyString((option as { text: unknown }).text);

/**
 * `data` is a JSON column with no database-level validation, so its shape has
 * to be checked here against the question `type` before it is stored.
 *
 * The selection limits on a choice question are bounded rather than free: the
 * minimum is at least 1 when the question is required and at least 0 when it
 * is not, and the maximum is exactly 1 for a single choice question and at
 * most the number of options for a multiple choice one. A single choice
 * question has only one valid value for each, so both are derived.
 */
const isCalendarEventFeedbackQuestionDataValid = ({
  data,
  isRequired,
  type
}: Params): boolean => {
  if (type === 'openquestion') return data === null || data === undefined;

  if (!data || typeof data !== 'object') return false;

  if (type === 'starrating') {
    const { numberOfStars } = data as { numberOfStars?: unknown };
    return isWholeNumber(numberOfStars) && numberOfStars > 0;
  }

  if (type === 'singlechoice' || type === 'multiplechoice') {
    const {
      numberOfAllowedSelectionsMax,
      numberOfAllowedSelectionsMin,
      options
    } = data as {
      numberOfAllowedSelectionsMax?: unknown;
      numberOfAllowedSelectionsMin?: unknown;
      options?: unknown;
    };

    if (!Array.isArray(options) || !options.length) return false;
    if (!options.every(isOption)) return false;

    if (
      !isWholeNumber(numberOfAllowedSelectionsMax) ||
      !isWholeNumber(numberOfAllowedSelectionsMin)
    ) {
      return false;
    }

    // a question that has to be answered needs at least one selection
    const smallestMin = isRequired ? 1 : 0;

    // a single choice question allows exactly one selection; a multiple choice
    // one cannot allow more selections than it has options
    const largestMax = type === 'singlechoice' ? 1 : options.length;

    return (
      numberOfAllowedSelectionsMin >= smallestMin &&
      numberOfAllowedSelectionsMax >= 1 &&
      numberOfAllowedSelectionsMax <= largestMax &&
      numberOfAllowedSelectionsMin <= numberOfAllowedSelectionsMax
    );
  }

  return false;
};

export default isCalendarEventFeedbackQuestionDataValid;
